/**
 * <TutorAbsenceConfirmStep> — « Confirme ton absence » (organism).
 *
 * L'étape `other_occurrences` de l'ancien `ManageRDVScreen` : la séance déclarée, cochée et figée ;
 * ce que l'absence change élève par élève (`<TutorAbsenceImpact>`, seulement si un élève change de
 * masteur) ; les crédits d'absence (`<TutorAbsenceCreditsNotice>`) ; la case à cocher, qui devient
 * « J'accepte de perdre mes lives » au-delà de la limite.
 *
 * La séance n'est pas une ligne de « Quelle séance ? » (`<SessionPickerStep>`) : celle-ci porte la
 * capsule et le prénom du tuteur, qui sont ici le masteur lui-même. Elle garde la carte d'option du
 * legacy : la date, la durée, les élèves.
 */
import React from "react"

import { Checkbox } from "../../atoms/Checkbox"
import { Stack } from "../../atoms/containers/Stack"
import { OptionCard } from "../../molecules/OptionCard"
import { TutorAbsenceCreditsNotice } from "../../molecules/TutorAbsenceCreditsNotice"
import { emptiesSession, hasTutorChange, TutorAbsenceImpact } from "../../molecules/TutorAbsenceImpact"
import { formatDayTime, formatDuration } from "../../../ports/primary/ui/formatters/dateFormatter"
import {
    type UITutorAbsenceCredits,
    type UITutorAbsenceImpactStudent,
    type UITutorWeekSession,
} from "../../../ports/primary/ui/types/tutorLives"

export type TutorAbsenceConfirmStepProps = {
    session: Pick<UITutorWeekSession, "date" | "duration" | "studentsLabel">
    impact: UITutorAbsenceImpactStudent[] | null
    /** Absent tant que le décompte n'est pas lu : la place reste vide. */
    credits: UITutorAbsenceCredits | null
    confirmed: boolean
    onToggleConfirmed: (next: boolean) => void
    onOpenCharter?: () => void
    testID?: string
}

export function TutorAbsenceConfirmStep(props: TutorAbsenceConfirmStepProps) {
    const { session, impact, credits, confirmed, onToggleConfirmed, onOpenCharter, testID } = props
    return (
        <Stack gap="3" testID={testID}>
            <OptionCard
                label={`${formatDayTime(session.date)} · ${formatDuration(session.duration)}`}
                description={session.studentsLabel ?? "(aucun élève)"}
                selected
                disabled
                onPress={() => {}}
            />
            <TutorAbsenceImpact students={impact} />
            {credits != null && (
                <TutorAbsenceCreditsNotice
                    credits={credits}
                    tutorChange={hasTutorChange(impact)}
                    emptiesSession={emptiesSession(impact)}
                    onOpenCharter={onOpenCharter}
                />
            )}
            <Checkbox
                checked={confirmed}
                onChange={onToggleConfirmed}
                label={
                    credits != null && credits.totalDays > credits.limit
                        ? "J'accepte de perdre mes lives"
                        : "Je confirme ma demande d'absence"
                }
            />
        </Stack>
    )
}
